import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import type { AssistantRoutineModelData } from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import type { IUpdateAssistantRoutineOperation } from "@/store/sync/operations/types";
import type { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

export class UpdateAssistantRoutineOperation extends BaseSyncOperation<IUpdateAssistantRoutineOperation> {
  get operationKind(): "UPDATE_ASSISTANT_ROUTINE" {
    return "UPDATE_ASSISTANT_ROUTINE";
  }

  override get waitForPullConfirmationBeforeOptimisticCleanup(): boolean {
    return true;
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const existingRoutine = await this.store.assistantRoutines.getAsync(this.payload.id);
    if (!existingRoutine) {
      logger.error({
        message: "Attempted to update assistant routine that does not exist",
        info: {
          operation: getLoggableOperation(this),
        },
      });
      return [];
    }

    const modelData = existingRoutine.modelData;
    const routineUpdate = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_ROUTINE",
        model_version: existingRoutine.modelVersion,
        model_data: {
          ...modelData,
          label: this.payload.label ?? modelData.label,
          description: this.payload.description ?? modelData.description,
          config_json: this.payload.config_json != null
            ? this.payload.config_json
            : modelData.config_json,
          status: this.payload.status ?? modelData.status,
          next_run_at: this.resolveNextRunAt(modelData),
          last_run_at: this.payload.last_run_at != null
            ? this.payload.last_run_at
            : modelData.last_run_at,
          updated_at: this.committedAt,
        },
        model_scopes: existingRoutine.modelScopes,
      },
    } as unknown as OptimisticSyncUpdate<AssistantRoutineModelData>;

    return [routineUpdate];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantRoutines.recompute(this.payload.id);
  }

  private resolveNextRunAt(modelData: AssistantRoutineModelData): string | null {
    if (this.payload.clear_next_run_at) {
      return null;
    }
    if (this.payload.next_run_at != null) {
      return this.payload.next_run_at;
    }

    return modelData.next_run_at;
  }
}
